
import Vue, { PropType } from 'vue';
import _trim from 'lodash/trim';
import { mapActions } from 'vuex';
import {
	FeedbackReport,
	FeedbackReportComment,
	FEEDBACK_REPORT_COMMENT_MAX_LENGTH,
} from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { getShowTextCounterAt } from '@/utils/fieldValidationUtils';
import FeedbackCommentItem from './FeedbackCommentItem.vue';
import FeedbackShotPicker from './FeedbackShotPicker.vue';
import { commentLengthRule, draftAfterSend, FeedbackThread, isCommentDeleted, threadComments } from './feedbackReports';
import { addComment, feedbackApiFailure } from './feedbackWrites';

// A feedback report's comments and a line for each status change, oldest first, as threads: each
// top-level comment or status line with its replies under it (one level: a reply to a reply joins
// the same thread). And the boxes to add a comment (with screenshots, chosen, pasted or dropped)
// or a reply, sent through restServer (./feedbackWrites.ts), which counts the reply on its thread.
export default Vue.extend({
	name: 'FeedbackComments',
	props: {
		report: { type: Object as PropType<FeedbackReport>, required: true },
		comments: { type: Array as PropType<FeedbackReportComment[]>, required: true },
		loading: { type: Boolean, default: false },
		error: { type: Boolean, default: false },
	},
	data () {
		return {
			MAX: FEEDBACK_REPORT_COMMENT_MAX_LENGTH,
			showCounterAt: getShowTextCounterAt(FEEDBACK_REPORT_COMMENT_MAX_LENGTH),
			rules: [commentLengthRule],
			draft: '',
			// screenshots to send with the comment
			shots: [] as File[],
			formValid: true,
			posting: false,
			// the top-level comment being replied to, or null
			replyingTo: null as string | null,
			replyDraft: '',
			replying: false,
			isMac: /Mac|iPhone|iPad/.test(navigator.platform || ''),
		};
	},
	computed: {
		threads (): FeedbackThread[] {
			return threadComments(this.comments);
		},
		canSendReply (): boolean {
			const body: string = _trim(this.replyDraft || '');
			return !!this.replyingTo && !!body && body.length <= FEEDBACK_REPORT_COMMENT_MAX_LENGTH && !this.replying;
		},
		canPost (): boolean {
			const body: string = _trim(this.draft || '');
			return (!!body || this.shots.length > 0) && body.length <= FEEDBACK_REPORT_COMMENT_MAX_LENGTH && this.formValid && !this.posting;
		},
	},
	watch: {
		// The comment being replied to was deleted, so it takes no more replies: the reply's words
		// move to the comment box rather than vanish.
		threads (threads: FeedbackThread[]) {
			if (!this.replyingTo || threads.some((thread: FeedbackThread) => thread.comment.id === this.replyingTo && !isCommentDeleted(thread.comment))) return;
			const words: string = _trim(this.replyDraft || '');
			this.cancelReply();
			if (!words) return;
			this.draft = this.draft ? `${this.draft}\n\n${words}` : words;
			this.updateSnackbar({
				status: SNACKBAR_STATUS.WARN,
				message: 'The comment you were replying to was deleted. Your reply is in the comment box.',
				show: true,
			});
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		isCommentDeleted,
		// Comments, and status lines that carry a note (a question, a reason), take replies, until
		// they are deleted.
		canReplyTo (comment: FeedbackReportComment): boolean {
			return (comment.kind === 'comment' || !!comment.body) && !isCommentDeleted(comment);
		},
		startReply (parent: FeedbackReportComment) {
			if (this.replyingTo !== parent.id) this.replyDraft = '';
			this.replyingTo = parent.id;
			this.$nextTick(() => {
				const boxes = this.$refs.replyBox as Vue | Vue[] | undefined;
				const box: Vue | undefined = Array.isArray(boxes) ? boxes[0] : boxes;
				const input: HTMLElement | null = box ? box.$el.querySelector('textarea') : null;
				if (input) input.focus();
			});
		},
		cancelReply () {
			this.replyingTo = null;
			this.replyDraft = '';
		},
		async sendReply () {
			if (!this.canSendReply || !this.replyingTo) return;
			this.replying = true;
			try {
				const failure = feedbackApiFailure(await addComment(this.report.number, _trim(this.replyDraft), { replyTo: this.replyingTo }));
				if (failure) {
					console.error('[feedback] failed to reply', this.report.id, this.replyingTo, failure.code);
					this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: failure.message, show: true });
					return;
				}
				this.cancelReply();
			} finally {
				this.replying = false;
			}
		},
		// A picture pasted into the comment box goes with the comment; text pastes as text
		onPaste (e: ClipboardEvent) {
			const picker = this.$refs.picker as (Vue & { addFromPaste: (event: ClipboardEvent) => boolean }) | undefined;
			if (picker && picker.addFromPaste(e)) e.preventDefault();
		},
		// Esc in the comment box: back to the page's shortcuts
		blur () {
			const draft = this.$refs.draft as Vue | undefined;
			const input: HTMLElement | null = draft ? draft.$el.querySelector('textarea') : null;
			if (input) input.blur();
		},
		focus () {
			const draft = this.$refs.draft as (Vue & { focus: () => void }) | undefined;
			if (draft) draft.focus();
		},
		async post () {
			if (!this.canPost) return;
			this.posting = true;
			try {
				const sent: string = this.draft;
				const shots: File[] = this.shots;
				const failure = feedbackApiFailure(await addComment(this.report.number, _trim(sent || ''), { shots }));
				if (failure) {
					console.error('[feedback] failed to comment', this.report.id, failure.code);
					this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: failure.message, show: true });
					return;
				}
				// a reply rescued into the box while this one sent (its parent was deleted) stays
				this.draft = draftAfterSend(this.draft, sent);
				this.shots = this.shots.filter((file: File) => !shots.includes(file));
			} finally {
				this.posting = false;
			}
		},
	},
	components: {
		FeedbackCommentItem,
		FeedbackShotPicker,
	},
});
